/**
 * Oak intercept cinematic — MVP PR 13b (H6).
 *
 * Cartridge canon: player walks north out of Pallet → steps onto the
 * Oak-trigger tile (one of two flanking tiles at the Pallet/Route 1
 * boundary) → Oak runs in from the north, dialogue, fade-to-black,
 * place in lab. Once in lab, Oak's "There are three POKéMON" speech
 * fires; control hands back; player walks to the table to pick.
 *
 * Self-gating: skip immediately if FLAG_OAK_INTRO_DONE is set (idempotent
 * if the trigger somehow re-fires; cartridge sets VAR_MAP_SCENE_PALLET_
 * TOWN_OAK = 1 at intro-end to make findTriggerAtTile bail on subsequent
 * walkovers, but the flag-check is the belt-and-suspenders).
 *
 * Reduced-motion variant: skip Oak's walk-down animation + the fade
 * durations; keep dialogue beats. Per user-locked decision 2026-05-13.
 *
 * Cartridge text used verbatim from
 * pokefirered/data/maps/PalletTown/text.inc — friend group is ≤10 and
 * the user explicitly authorized verbatim copy of the IP-shaded text.
 */
import type { Handler, ScriptCtx } from '../scriptRunner';
import type { Tile } from '../npcWalkPath';
import { effectsReduced } from '../../render/motion';
import { setLabSceneState, LAB_SCENE_STATES } from '../labScene';

/** Page-set for cinematic dialogue. Each top-level array is one
 *  showDialogue invocation; the inner strings are the pages within. */
const OAK_DONT_GO_OUT = [
  'OAK: Hey! Wait!',
  "Don't go out!",
];

const OAK_GRASS_UNSAFE = [
  "OAK: It's unsafe!",
  'Wild POKéMON live in tall grass!',
  'You need your own POKéMON for your protection.',
  'I know! Here, come with me!',
];

// Cartridge PalletTown_ProfessorOaksLab_Text_OakThreeMonsChooseOne,
// preserved page-by-page (cartridge `\p` = new page) and stripped of
// the leading rival beat ("OAK: {RIVAL}? Let me think…" + the "Just
// wait!" half of the next line) per CLAUDE.md locked decision: no
// post-Pallet rival, BLUE is the single fixed name. The opening line
// is shortened from cartridge "Oh, that's right, I told you to come!
// Just wait!" to drop the "Just wait!" half (Oak isn't waiting for
// anyone here).
// Cartridge PalletTown_ProfessorOaksLab_Text_RivalFedUpWithWaiting,
// fires once before Oak's monologue. Two pages so the second-tap pacing
// matches cartridge (separate \n + $ in text.inc reads as one msgbox
// with internal scrolling; we render as paged dialogue).
const RIVAL_FED_UP = [
  'RIVAL: Gramps!',
  "I'm fed up with waiting!",
];

// Cartridge PalletTown_ProfessorOaksLab_Text_RivalNoFairWhatAboutMe,
// fires after Oak's monologue. The rival's react-bob animation
// (Movement_RivalReact, walk_in_place_up × 2) plays just before this.
const RIVAL_NO_FAIR = [
  'RIVAL: Hey! Gramps!',
  'No fair! What about me?',
];

// Cartridge PalletTown_ProfessorOaksLab_Text_OakBePatientRival, Oak's
// response to the rival's complaint. Closes the pre-pick monologue
// sequence — state advances to 2 (PRE_PICK) after this beat.
const OAK_BE_PATIENT = [
  'OAK: Be patient, BLUE.',
  'You can have one, too!',
];

// Localized rival NPC in the lab runtime.json. Used for the
// react-bob animation via setNpcFrame.
const RIVAL_LOCAL_ID = 'LOCALID_OAKS_LAB_RIVAL';

// directional-9 frame indices per npcFacing.frameForFacing +
// walkCycleFrame. Pre-computed so the react-bob loop reads as data,
// not a derivation chain. See apps/game/src/scripts/npcFacing.ts for
// the cartridge layout (FaceSouth=0, FaceNorth=1, FaceWest=2, then
// walk frames 3-8 per direction).
const RIVAL_STANDING_UP_FRAME = 1;
const RIVAL_WALK_UP_FRAME_A = 5;

const OAK_LAB_SPEECH = [
  "OAK: Oh, that's right, I told you to come!",
  'Here, RED.',
  'There are three POKéMON here.',
  'Haha!',
  'The POKéMON are held inside these POKé BALLS.',
  'When I was young, I was a serious POKéMON TRAINER.',
  'But now, in my old age, I have only these three left.',
  'You can have one.',
  'Go on, choose!',
];

const VAR_PALLET_OAK_RETIRED = 'VAR_MAP_SCENE_PALLET_TOWN_OAK';

/** Where the player stands for the monologue + draft: directly south
 *  of Oak's lab NPC at (6, 3). Column 6 is a clean walkable channel
 *  from the warp-in tile (6, 11) — verified against the lab collision
 *  grid in labCinematicData.test.ts. */
const OAK_FRONT = { x: 6, y: 4 } as const;

export const oakInterceptHandler: Handler = async (ctx: ScriptCtx, ref: string) => {
  if (ctx.runState.hasFlag(ctx.FLAGS.OAK_INTRO_DONE)) return;

  await ctx.worldScene.runHandlerBracket(async () => {
    // Reduced-motion attenuates the walk speed (~2x faster) but does NOT
    // skip the walk story beat — the user explicitly asked for "Oak walks
    // with me" to always play (2026-05-14). Story beats render under
    // every motion preference; only the duration shortens. The door-
    // transition fade still respects reducedMotion internally via
    // doorTransition.
    const walkSpeed = effectsReduced() ? 192 : 96;
    const player = ctx.worldScene.getPlayerTile();
    const isLeftTrigger = ref === 'PalletTown_EventScript_OakTriggerLeft';

    // Beat 1: "Hey! Wait!"
    ctx.worldScene.showDialogue(OAK_DONT_GO_OUT, null);
    await ctx.worldScene.waitForDialogueClose();

    // Beat 2: Oak runs UP from off-screen south, mirroring the cartridge
    // OakEnterLeft / OakEnterRight movements (Oak's home tile in
    // PalletTown is (10, 8); cinematic starts him there and runs him up
    // to one tile south of the trigger). To make the entry read as "from
    // off screen" at any zoom level, we extend his spawn point ~5 tiles
    // further south on the col-10 channel (west of the lab building), so
    // even at max-zoom portrait he starts beyond the bottom of the
    // camera and walks into view. The cartridge zig-zag (up 2 / right 1)
    // takes him through walkable Pallet tiles, avoiding the lab building
    // footprint.
    //
    // The two trigger refs end Oak at the same relative offset
    // (player.x, player.y + 1) — one tile south of whichever flank
    // fired. That positions him one path-step ahead of the player for
    // the lockstep lead-walk in beat 4.
    // Spawn x matches cartridge Oak home (col 10 in Pallet-local) for
    // both triggers. y is extended 5 tiles past cartridge home so Oak
    // starts past the bottom of the camera at any reasonable zoom (zoom
    // 3 portrait still has ~8 visible tiles below player; y+12 is past
    // that). Walk path replays cartridge OakEnterLeft / OakEnterRight
    // ending with an UP step so Oak finishes facing the player.
    const oakSpawn: Tile = {
      x: player.x - (isLeftTrigger ? 2 : 3),
      y: player.y + 12,
    };
    const oakEntryWaypoints: readonly Tile[] = isLeftTrigger
      ? [
          // (10, 13) → (10, 6): up 7 (consolidates the col-10 ascent
          // through cartridge home (10, 8) into one straight run).
          { x: player.x - 2, y: player.y + 5 },
          { x: player.x - 1, y: player.y + 5 }, // right 1 → (11, 6)
          { x: player.x - 1, y: player.y + 3 }, // up 2    → (11, 4)
          { x: player.x,     y: player.y + 3 }, // right 1 → (12, 4)
          { x: player.x,     y: player.y + 1 }, // up 2    → (12, 2) facing up
        ]
      : [
          // (10, 13) → (10, 8): up 5 to cartridge home, then OakEnterRight.
          { x: player.x - 3, y: player.y + 7 },
          { x: player.x - 2, y: player.y + 7 }, // right 1 → (11, 8)
          { x: player.x - 2, y: player.y + 5 }, // up 2    → (11, 6)
          { x: player.x - 1, y: player.y + 5 }, // right 1 → (12, 6)
          { x: player.x - 1, y: player.y + 3 }, // up 2    → (12, 4)
          { x: player.x,     y: player.y + 3 }, // right 1 → (13, 4)
          { x: player.x,     y: player.y + 1 }, // up 2    → (13, 2) facing up
        ];
    const oakSpriteHandle = await ctx.worldScene.npcWalkAlongPath({
      spriteKey: 'prof_oak',
      startTile: oakSpawn,
      waypoints: oakEntryWaypoints,
      pixelsPerSecond: walkSpeed,
    });

    // Player pivots south to face Oak before the next dialogue. Cartridge
    // doesn't explicitly do this (player stays facing up), but on a small
    // phone screen the "back to the speaker" framing reads as a bug. The
    // turn is instant — no extra walk beat.
    ctx.worldScene.setPlayerFacing('down');

    // Beat 3: "It's unsafe! ... come with me!"
    ctx.worldScene.showDialogue(OAK_GRASS_UNSAFE, null);
    await ctx.worldScene.waitForDialogueClose();

    // Beat 4: cartridge lead-walk to the lab. Pallet's lab building
    // (~cols 12-15, rows 8-12 in PalletTown-local) blocks a straight
    // south shot from either trigger tile (left at (12,1), right at
    // (13,1)), so the cartridge routes WEST around the building via the
    // col-11 channel, south down to row 14, then east to the door at
    // (16, 13). Both triggers converge at the same door tile (left goes
    // west 1, right goes west 2).
    //
    // Lockstep: Oak starts already one tile south of the player (beat 2
    // put him at (px, py+1)). Each step both move simultaneously via
    // Promise.all with matching pixelsPerSecond; Oak's path has the same
    // total step count as the player's because he saves a step at the
    // start (south 1 vs player's south 2) and spends an extra at the end
    // (east 5 vs player's east 4), landing him one tile east of the
    // player on row 14. From there Oak takes the door-up step alone,
    // destroys ("walks into the lab"), and the player follows east + up
    // onto the door tile.
    //
    // Final player facing is 'up' (last step is the UP onto the door),
    // which is what transitionToMap wants for the lab entry.
    const westDx = isLeftTrigger ? -1 : -2;
    const doglegEndX = player.x + westDx;
    const doglegEndY = player.y + 13;
    const doorX = player.x + westDx + 5; // 16 in PalletTown-local
    const doorY = player.y + 12;          // 13 in PalletTown-local

    // Stage A — lockstep parallel dogleg. Player steps 2 + |dx| + 11 + 4;
    // Oak steps 1 + |dx| + 11 + 5. Same total; Oak is always one
    // path-position ahead, leading the walk.
    const playerDogleg: readonly Tile[] = [
      { x: player.x, y: player.y + 2 },          // south 2
      { x: doglegEndX, y: player.y + 2 },         // west dx
      { x: doglegEndX, y: doglegEndY },           // south 11 → (11, 14)
      { x: doorX - 1, y: doglegEndY },            // east 4 → (15, 14)
    ];
    const oakDogleg: readonly Tile[] = [
      { x: player.x, y: player.y + 2 },          // south 1 (start row+1)
      { x: doglegEndX, y: player.y + 2 },         // west dx
      { x: doglegEndX, y: doglegEndY },           // south 11 → (11, 14)
      { x: doorX, y: doglegEndY },                // east 5 → (16, 14)
    ];
    await Promise.all([
      ctx.worldScene.walkPlayerAlongPath({
        waypoints: playerDogleg,
        pixelsPerSecond: walkSpeed,
      }),
      ctx.worldScene.npcWalkAlongPath({
        existingSprite: oakSpriteHandle.sprite,
        spriteKey: 'prof_oak', // ignored when existingSprite is set
        startTile: { x: player.x, y: player.y + 1 },
        waypoints: oakDogleg,
        pixelsPerSecond: walkSpeed,
      }),
    ]);

    // Stage B — Oak takes the final up-step onto the door alone (the
    // cartridge walk_up + set_invisible beat from OakEnterLab). Player
    // paused at (doorX-1, doglegEndY) facing right for ~1 tile-time.
    await ctx.worldScene.npcWalkAlongPath({
      existingSprite: oakSpriteHandle.sprite,
      spriteKey: 'prof_oak',
      startTile: { x: doorX, y: doglegEndY },
      waypoints: [{ x: doorX, y: doorY }], // up 1 → door tile
      pixelsPerSecond: walkSpeed,
    });

    // Stage C — Oak "vanishes into the lab." The post-warp lab map has
    // its own Oak NPC (visible after FLAG_HIDE_OAK_IN_HIS_LAB clears
    // below) so the handover reads continuous.
    oakSpriteHandle.sprite.destroy();

    // Stage D — player follows Oak through the door. East 1 onto Oak's
    // old tile, then up 1 onto the door tile facing up.
    await ctx.worldScene.walkPlayerAlongPath({
      waypoints: [
        { x: doorX, y: doglegEndY }, // east 1 → (16, 14)
        { x: doorX, y: doorY },       // up 1 → (16, 13) door tile
      ],
      pixelsPerSecond: walkSpeed,
    });

    // Beat 5: flag state mutation. Cartridge order: set the trigger-
    // retired var FIRST, then flip visibility flags. Doing it before
    // the transition means the destination map's NPC rebuild (inside
    // transitionToMap) sees the new state and renders Oak in the lab
    // correctly on fade-in.
    ctx.runState.setFlag(ctx.FLAGS.OAK_INTRO_DONE);
    ctx.runState.setFlag(ctx.FLAGS.HIDE_OAK_IN_PALLET_TOWN);
    ctx.runState.clearFlag(ctx.FLAGS.HIDE_OAK_IN_HIS_LAB);
    // Lab pedestals get their balls once Oak brings the player back to
    // the lab. Pre-cinematic the lab is empty (INITIAL_HIDDEN_FLAGS seeds
    // these as set); clearing here is what populates the table for the
    // upcoming starter draft. DraftScene.commit re-sets them on pick.
    ctx.runState.clearFlag(ctx.FLAGS.HIDE_BULBASAUR_BALL);
    ctx.runState.clearFlag(ctx.FLAGS.HIDE_SQUIRTLE_BALL);
    ctx.runState.clearFlag(ctx.FLAGS.HIDE_CHARMANDER_BALL);
    // Rival has been "waiting" in the lab the whole time (cartridge
    // canon — see PalletTown_ProfessorOaksLab_ChooseStarterScene). Cleared
    // alongside Oak so the player sees him at his spawn tile (5, 4)
    // from the moment the cinematic deposits them in the lab. The PR-15
    // rivalLab handler self-gates on FLAG_STARTER_PICKED so he's just
    // standing there until the pick happens.
    ctx.runState.clearFlag(ctx.FLAGS.HIDE_RIVAL_IN_LAB);
    ctx.runState.setVar(VAR_PALLET_OAK_RETIRED, '1');

    // Beat 6: door-open beat (cartridge sfx + brief pause) + warp into
    // lab. doorTransition's existing fade IS the cartridge door-open
    // animation visually. Oak sprite was already destroyed in beat 4
    // stage C; the new lab map has its own Oak NPC (now visible).
    await ctx.worldScene.transitionToMap(
      'PalletTown_ProfessorOaksLab',
      6,
      12,
      'up',
    );

    // CARTRIDGE STATE 1 — MONOLOGUE_PENDING. Set synchronously with the
    // warp landing (before any delay) so a tap on the rival during the
    // 60-frame pacing pause below resolves against state 1 ("Gramps!
    // Fed up!"), not the state-0 defensive fallback. Per eng-review
    // decision 1 (PR A2 beat 6a).
    setLabSceneState(LAB_SCENE_STATES.MONOLOGUE_PENDING);

    // Beat 6b — cartridge `delay 60` after warp + before the first
    // rival interjection. Sells "rival has been waiting in here while
    // Oak walked you home." 60 frames @ 60fps ≈ 1000ms.
    await ctx.worldScene.delay(1000);

    // Beat 6c — rival interjection BEFORE Oak's monologue. Cartridge
    // Text_RivalFedUpWithWaiting.
    ctx.worldScene.showDialogue(RIVAL_FED_UP, null);
    await ctx.worldScene.waitForDialogueClose();

    // Beat 6e — cartridge `delay 30` between interjection close and
    // Oak's monologue. ~500ms.
    await ctx.worldScene.delay(500);

    // Beat 6e2 — lab-flow redesign (third playtest 2026-08-02): the
    // player walks up to Oak BEFORE the monologue, so the speech and
    // the draft that follows it happen face-to-face instead of shouted
    // across the room from the door. Column 6 is walkable from the
    // warp-in tile (6, 11) straight north to (6, 4) — one tile south
    // of Oak's (6, 3). The rival stands adjacent at (5, 4), which is
    // exactly the cartridge staging for his "No fair!" complaint.
    await ctx.worldScene.walkPlayerAlongPath({
      waypoints: [{ x: OAK_FRONT.x, y: OAK_FRONT.y }],
      pixelsPerSecond: walkSpeed,
      // Round-4 spin guard: these waypoints are lab-local. If the
      // player is somehow not in the lab (a hold regression), the walk
      // no-ops instead of driving the sprite across the wrong map.
      expectedMap: 'PalletTown_ProfessorOaksLab',
    });

    // Beat 6f — Oak's "Pick a starter" monologue (cartridge
    // Text_OakThreeMonsChooseOne, 9 pages, shipped in commit 9384b27).
    ctx.worldScene.showDialogue(OAK_LAB_SPEECH, null);
    await ctx.worldScene.waitForDialogueClose();

    // Beat 6h — cartridge `delay 30` before rival's react-bob.
    await ctx.worldScene.delay(500);

    // Beat 6i — rival react animation. Cartridge Movement_RivalReact
    // is walk_in_place_up × 2 + step_end; here we toggle the static
    // rival NPC sprite between walkA-up and standing-up at a 125ms
    // cadence so the animation reads as "rival bounces angrily in
    // place." The sprite settles on standing-up at the end so the
    // post-bob dialogue mounts with the rival facing the player.
    ctx.worldScene.setNpcFrame(RIVAL_LOCAL_ID, RIVAL_WALK_UP_FRAME_A);
    await ctx.worldScene.delay(125);
    ctx.worldScene.setNpcFrame(RIVAL_LOCAL_ID, RIVAL_STANDING_UP_FRAME);
    await ctx.worldScene.delay(125);
    ctx.worldScene.setNpcFrame(RIVAL_LOCAL_ID, RIVAL_WALK_UP_FRAME_A);
    await ctx.worldScene.delay(125);
    ctx.worldScene.setNpcFrame(RIVAL_LOCAL_ID, RIVAL_STANDING_UP_FRAME);

    // Beat 6j — rival complains, Oak responds. Two dialogue groups so
    // the player taps through "No fair!" before Oak's "Be patient!"
    // — cartridge separates these into distinct msgbox calls.
    ctx.worldScene.showDialogue(RIVAL_NO_FAIR, null);
    await ctx.worldScene.waitForDialogueClose();
    ctx.worldScene.showDialogue(OAK_BE_PATIENT, null);
    await ctx.worldScene.waitForDialogueClose();

    // CARTRIDGE STATE 2 — PRE_PICK. The runtime.json LeaveStarterScene
    // coord-event triggers (varValue=2) become eligible — walking south
    // fires the leave-guard handler ("OAK: Hey! Don't go away yet!").
    setLabSceneState(LAB_SCENE_STATES.PRE_PICK);

    ctx.worldScene.save();
  });

  // Lab-flow redesign (third playtest 2026-08-02): the draft modal
  // fires DIRECTLY at the end of Oak's speech — the player is standing
  // in front of him; no ball-touching required. Fired AFTER the
  // bracket releases so the modal's close → WAKE → labPostPick
  // dispatch runs against a clean (non-transitioning) scene, exactly
  // like the old ball-tap path did.
  //
  // The offers are rolled asynchronously by onMapEntered during the
  // warp-in; on any sane device they're cached long before the 9-page
  // speech ends. Poll briefly for the pathological case; if it still
  // isn't ready, the state-aware Oak tap is the player-facing retry
  // (tap Oak → modal), so this can degrade without stranding anyone.
  //
  // Round-4: the poll runs OUTSIDE the bracket, so hold the input gate
  // across it — the slow path yields 50ms frames that were briefly
  // walkable ("no movement control during the intro"). Released in a
  // finally, and launchStarterDraft's first synchronous line raises
  // encounterPending, so no input frame exists between the two gates.
  ctx.worldScene.setTransitioning(true);
  try {
    for (let i = 0; i < 100; i++) {
      const pending = ctx.runState.getPendingDraft();
      if (pending?.zoneId === 'PalletTown_ProfessorOaksLab') break;
      await ctx.worldScene.delay(50);
    }
  } finally {
    ctx.worldScene.setTransitioning(false);
  }
  await ctx.worldScene.launchStarterDraft();
};
